import React, { useState, useMemo, useRef } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import Card from "components/card";
import { DiApple, DiAndroid, DiWindows } from "react-icons/di";
import { IoClose } from "react-icons/io5";
import ViewResponse from "./ViewResponse";
import AxiosInstance from "axiosConfig";

// Constants
const NODE_FIELDS = [
  {
    name: "node_key_words",
    label: "Node Keywords",
    type: "textarea",
    required: true,
    rows: 2,
    maxLength: 400,
  },
  {
    name: "ans_desc",
    label: "Answer Description",
    type: "textarea",
    required: true,
    rows: 3,
    maxLength: 1000,
  },
  {
    name: "que_topic",
    label: "Question Topic",
    type: "select",
    required: true,
    options: ["", "topic1", "topic2", "topic3"],
    defaultLabel: "Select Topic",
  },
  {
    name: "bot_act_as",
    label: "Bot Act As",
    type: "select",
    required: true,
    options: ["", "assistant", "advisor", "expert"],
    defaultLabel: "Select Role",
  },
  {
    name: "que_category",
    label: "Question Category",
    type: "select",
    required: true,
    options: ["", "general", "technical", "specific"],
    defaultLabel: "Select Category",
  },
  {
    name: "culture",
    label: "Culture",
    type: "select",
    required: true,
    options: ["", "global", "regional", "local"],
    defaultLabel: "Select Culture",
  },
  {
    name: "enforces_match",
    label: "Enforces Match",
    type: "checkbox",
  },
  {
    name: "comments",
    label: "Comments",
    type: "textarea",
    rows: 2,
    maxLength: 300,
  },
];

// Components
const Dialog = ({ isOpen, onClose, title, message, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-md w-full max-w-md p-6">
        <h2 className="text-xl font-semibold mb-4">{title}</h2>
        <p className="text-gray-700 mb-6">{message}</p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Close
          </button>
          {onConfirm && (
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Confirm
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const NodeMaintenanceModal = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  errors,
  setErrors,
}) => {
  const modalRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    NODE_FIELDS.forEach((field) => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    } else {
      setErrors((prev) => ({
        ...prev,
        general: "Please fill in all required fields correctly.",
      }));
    }
  };

  const insertVariable = (fieldName, variable) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: (prev[fieldName] || "") + variable,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-md w-full max-w-2xl max-h-[80vh] flex flex-col overflow-y-auto"
      >
        <div className="flex justify-between items-center p-6 pb-0">
          <h2 className="text-2xl font-semibold">Add Node</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close modal"
          >
            <IoClose size={24} />
          </button>
        </div>
        <div className="p-6 flex-1">
          <form onSubmit={handleSubmit} className="space-y-4">
            <fieldset className="mb-4">
              <legend className="text-lg font-semibold mb-2">Node Information</legend>
              {NODE_FIELDS.filter((field) =>
                ["node_key_words", "ans_desc"].includes(field.name)
              ).map((field) => (
                <div key={field.name} className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && errors[field.name] && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  <textarea
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleInputChange}
                    rows={field.rows}
                    maxLength={field.maxLength}
                    className={`w-full p-2 border rounded-lg ${
                      errors[field.name] ? "border-red-500" : "border-blue-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                  {field.name === "ans_desc" && (
                    <span
                      className="text-blue-600 cursor-pointer underline text-sm mt-1 inline-block"
                      onClick={() => insertVariable("ans_desc", "/Name/")}
                    >
                      @FirstName
                    </span>
                  )}
                  {errors[field.name] && (
                    <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>
                  )}
                </div>
              ))}
            </fieldset>
            <fieldset className="mb-4">
              <legend className="text-lg font-semibold mb-2">Node Options</legend>
              <div className="grid grid-cols-2 gap-4">
                {NODE_FIELDS.filter((field) =>
                  [
                    "que_topic",
                    "bot_act_as",
                    "que_category",
                    "culture",
                    "enforces_match",
                  ].includes(field.name)
                ).map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.required && errors[field.name] && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    {field.type === "select" ? (
                      <select
                        name={field.name}
                        value={formData[field.name] || ""}
                        onChange={handleInputChange}
                        className={`w-full p-2 border rounded-lg ${
                          errors[field.name] ? "border-red-500" : "border-blue-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      >
                        <option value="" disabled>
                          {field.defaultLabel}
                        </option>
                        {field.options.slice(1).map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name={field.name}
                          checked={formData[field.name] || false}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{field.label}</span>
                      </div>
                    )}
                    {errors[field.name] && (
                      <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>
                    )}
                  </div>
                ))}
              </div>
            </fieldset>
            <fieldset className="mb-4">
              <legend className="text-lg font-semibold mb-2">Additional Details</legend>
              {NODE_FIELDS.filter((field) => field.name === "comments").map(
                (field) => (
                  <div key={field.name} className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    <textarea
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleInputChange}
                      rows={field.rows}
                      maxLength={field.maxLength}
                      className="w-full p-2 border rounded-lg border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )
              )}
            </fieldset>
            <button
              type="submit"
              className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Submit Node
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const DevelopmentTable = (props) => {
  const { columnsData, tableData } = props;
  const [modalData, setModalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [formData, setFormData] = useState({
    node_key_words: "",
    ans_desc: "",
    que_topic: "",
    bot_act_as: "",
    que_category: "",
    culture: "",
    enforces_match: false,
    comments: "",
  });
  const [errors, setErrors] = useState({});
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const handleViewClick = async (rowData) => {
    try {
      const response = await AxiosInstance.post("/view-response", {
        for_message_id: rowData.message_id,
        from_sender: rowData.sender,
        channel_name: rowData.channel,
      });
      setModalData(response.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      setDialogConfig({
        isOpen: true,
        title: "Error",
        message: `Failed to fetch response: ${error.message || error}`,
        onConfirm: null,
      });
    }
  };

  const handleTrainClick = (rowData) => {
    setFormData({
      node_key_words: rowData.message.toLowerCase()|| "",
      ans_desc: "",
      que_topic: "",
      bot_act_as: "",
      que_category: "",
      culture: "",
      enforces_match: false,
      comments: "",
    });
    setErrors({});
    setShowNodeModal(true);
  };

  const handleNodeSubmit = async (data) => {
    try {
      const nodeData = {
        node_key_words: data.node_key_words,
        ans_desc: data.ans_desc,
        que_topic: data.que_topic,
        bot_act_as: data.bot_act_as,
        que_category: data.que_category,
        culture: data.culture,
        enforces_match: data.enforces_match,
        comments: data.comments,
      };

      const response = await AxiosInstance.post("/node-creation", nodeData);

      setDialogConfig({
        isOpen: true,
        title: "Success",
        message: response.data?.message || "Node created successfully!",
        onConfirm: () => {
          setShowNodeModal(false);
          setFormData({
            node_key_words: "",
            ans_desc: "",
            que_topic: "",
            bot_act_as: "",
            que_category: "",
            culture: "",
            enforces_match: false,
            comments: "",
          });
          setErrors({});
        },
      });
    } catch (err) {
      setDialogConfig({
        isOpen: true,
        title: "Error",
        message: `Failed to create node: ${err.message || err}`,
        onConfirm: null,
      });
    }
  };

  const columns = useMemo(() => {
    return [
      ...columnsData,
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleViewClick(row.original)}
              className="text-blue-500 hover:underline cursor-pointer"
            >
              View
            </button>
            <span>|</span>
            <button
              onClick={() => handleTrainClick(row.original)}
              className="text-blue-500 hover:underline cursor-pointer"
            >
              Train
            </button>
          </div>
        ),
      },
    ];
  }, [columnsData]);

  const data = useMemo(() => tableData, [tableData]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 12 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
  } = tableInstance;

  return (
    <>
      <Card extra="w-full h-full p-4">
        <div className="relative flex items-center justify-between">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            Messages Received
          </div>
        </div>

        <div className="h-full overflow-x-auto">
          <table
            {...getTableProps()}
            className="mt-8 w-full border-collapse"
          >
            <thead>
              {headerGroups.map((headerGroup, index) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className="border-b border-gray-200 pr-8 pb-[10px] text-left dark:!border-navy-700"
                      key={index}
                    >
                      <div className="text-xs font-bold tracking-wide text-gray-600">
                        {column.render("Header")}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    key={index}
                    className={index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"}
                  >
                    {row.cells.map((cell, index) => (
                      <td
                        {...cell.getCellProps()}
                        key={index}
                        className="pt-[14px] pb-3 text-[14px] text-left"
                      >
                        {cell.column.Header === "VIA" ? (
                          <div className="flex items-center justify-center gap-2">
                            {cell.value === "channel" && (
                              <div className="text-[22px] text-gray-600 dark:text-white">
                                <DiApple />
                              </div>
                            )}
                            {cell.value === "android" && (
                              <div className="text-[21px] text-gray-600 dark:text-white">
                                <DiAndroid />
                              </div>
                            )}
                            {cell.value === "windows" && (
                              <div className="text-xl text-gray-600 dark:text-white">
                                <DiWindows />
                              </div>
                            )}
                          </div>
                        ) : (
                          cell.render("Cell")
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between mt-4">
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className={`${
              !canPreviousPage && "cursor-not-allowed opacity-50"
            } border border-gray-300 rounded-md px-4 py-2 text-gray-700 bg-white hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600 focus:outline-none`}
          >
            Previous
          </button>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className={`${
              !canNextPage && "cursor-not-allowed opacity-50"
            } border border-gray-300 rounded-md px-4 py-2 text-gray-700 bg-white hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600 focus:outline-none`}
          >
            Next
          </button>
        </div>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full relative">
            <button
              className="absolute top-0 right-0 mt-2 mr-2 text-gray-500 hover:text-gray-700"
              onClick={() => setIsModalOpen(false)}
              aria-label="Close modal"
            >
              <IoClose size={24} />
            </button>
            <ViewResponse response={modalData} />
          </div>
        </div>
      )}

      <NodeMaintenanceModal
        isOpen={showNodeModal}
        onClose={() => setShowNodeModal(false)}
        onSubmit={handleNodeSubmit}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
      />

      <Dialog
        isOpen={dialogConfig.isOpen}
        onClose={() => setDialogConfig((prev) => ({ ...prev, isOpen: false }))}
        title={dialogConfig.title}
        message={dialogConfig.message}
        onConfirm={dialogConfig.onConfirm}
      />
    </>
  );
};
export default DevelopmentTable;